import React, { useState } from "react";
import { Link } from "react-router-dom";
import { API, getToken } from "@/lib/api";
import { Check, Zap } from "lucide-react";
import { toast } from "sonner";

const PRO_FEATURES = [
  "Unlimited hook, remix & score generations",
  "Full 25-item trends across all 8 regions",
  "Voice-to-content transcription (Whisper)",
  "Save unlimited items to the Vault",
  "Priority queue on Claude Sonnet 4.5",
  "Early access to upcoming modules",
];

const FREE_FEATURES = [
  "5 generations per day",
  "Save up to 10 items",
  "US trends only",
  "Standard Claude latency",
];

const TEAM_FEATURES = [
  "Everything in Pro",
  "5 team seats included",
  "Shared vault + brand voice",
  "Team leaderboard + analytics",
  "Priority support",
];

async function startCheckoutWithKey(lookup_key) {
  const res = await fetch(`${API}/payments/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ lookup_key, origin_url: window.location.origin }),
  });
  if (!res.ok) throw new Error(`Checkout failed: ${res.status}`);
  const data = await res.json();
  window.location.href = data.checkout_url;
}

export default function Pricing({ compact = false }) {
  const [cycle, setCycle] = useState("monthly");
  const price = cycle === "annual" ? { amount: 190, per: "yr", lookup: "pro_annual", sub: "just $15.83/mo — 2 months free" }
                                   : { amount: 19,  per: "mo", lookup: "pro_monthly", sub: "cancel anytime" };

  const upgrade = async (key = price.lookup) => {
    try { await startCheckoutWithKey(key); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className={compact ? "space-y-6" : "p-6 md:p-10 lg:p-12 space-y-10"} data-testid="pricing-page">
      {!compact && (
        <header className="border-b border-white/10 pb-6">
          <div className="label-eyebrow">// upgrade</div>
          <h1 className="heading-display text-4xl sm:text-5xl mt-2">GO <span className="text-signal-yellow">PRO</span>.</h1>
          <p className="font-mono text-xs text-zinc-500 mt-3">// ship faster. more hooks. no limits.</p>
        </header>
      )}

      <div className="inline-flex border border-white/15 p-1" data-testid="cycle-toggle">
        {[
          { v: "monthly", l: "Monthly", tag: "" },
          { v: "annual", l: "Annual", tag: "SAVE 17%" },
        ].map((o) => (
          <button
            key={o.v}
            data-testid={`cycle-${o.v}`}
            onClick={() => setCycle(o.v)}
            className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors flex items-center gap-2 ${
              cycle === o.v ? "bg-signal-yellow text-black" : "text-zinc-400 hover:text-white"
            }`}
          >
            {o.l}
            {o.tag && <span className={`text-[9px] ${cycle === o.v ? "text-black/70" : "text-signal-yellow"}`}>{o.tag}</span>}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-6xl">
        {/* Free */}
        <div className="bg-[#141414] p-8 space-y-5" data-testid="plan-free">
          <div>
            <div className="label-eyebrow">// free</div>
            <h2 className="heading-display text-3xl mt-2">STARTER</h2>
            <div className="font-mono text-4xl font-bold mt-3">$0<span className="text-zinc-500 text-sm">/mo</span></div>
          </div>
          <ul className="space-y-3">
            {FREE_FEATURES.map((f) => (
              <li key={f} className="flex gap-3 font-mono text-xs text-zinc-400">
                <Check size={14} className="text-zinc-600 shrink-0 mt-0.5" /><span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="pt-2 font-mono text-[10px] text-zinc-600 tracking-[0.2em] uppercase">// active plan</div>
        </div>

        {/* Pro */}
        <div className="bg-[#0A0A0A] border-2 border-signal-yellow p-8 space-y-5 relative" data-testid="plan-pro">
          <div className="absolute -top-3 left-6 bg-signal-yellow text-black font-mono text-[10px] tracking-[0.25em] uppercase px-2 py-1">
            Recommended
          </div>
          <div>
            <div className="label-eyebrow text-signal-yellow">// pro</div>
            <h2 className="heading-display text-3xl mt-2">VIRAL/CO PRO</h2>
            <div className="font-mono text-4xl font-bold mt-3 text-signal-yellow" data-testid="plan-pro-price">
              ${price.amount}<span className="text-zinc-500 text-sm">/{price.per}</span>
            </div>
            <p className="font-mono text-[10px] text-zinc-500 mt-1">// {price.sub}</p>
          </div>
          <ul className="space-y-3">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex gap-3 font-mono text-xs text-zinc-200">
                <Check size={14} className="text-signal-yellow shrink-0 mt-0.5" /><span>{f}</span>
              </li>
            ))}
          </ul>
          <button
            data-testid="upgrade-btn"
            onClick={() => upgrade()}
            className="w-full bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-6 py-4 hover:bg-signal-yellow-hover transition-colors flex items-center justify-center gap-2"
          >
            <Zap size={14} /> Upgrade to Pro →
          </button>
          <p className="font-mono text-[10px] text-zinc-600 text-center">// test card: 4242 4242 4242 4242 · any future date · any cvc</p>
        </div>

        {/* Team */}
        <div className="bg-[#141414] p-8 space-y-5" data-testid="plan-team">
          <div>
            <div className="label-eyebrow">// team</div>
            <h2 className="heading-display text-3xl mt-2">TEAM</h2>
            <div className="font-mono text-4xl font-bold mt-3" data-testid="plan-team-price">
              $49<span className="text-zinc-500 text-sm">/mo</span>
            </div>
            <p className="font-mono text-[10px] text-zinc-500 mt-1">// up to 5 seats</p>
          </div>
          <ul className="space-y-3">
            {TEAM_FEATURES.map((f) => (
              <li key={f} className="flex gap-3 font-mono text-xs text-zinc-300">
                <Check size={14} className="text-signal-yellow shrink-0 mt-0.5" /><span>{f}</span>
              </li>
            ))}
          </ul>
          <button
            data-testid="team-btn"
            onClick={() => upgrade("team_monthly")}
            className="w-full border border-signal-yellow text-signal-yellow font-bold uppercase tracking-[0.18em] text-xs px-6 py-4 hover:bg-signal-yellow hover:text-black transition-colors"
          >
            Upgrade to Team →
          </button>
        </div>
      </div>

      {!compact && (
        <p className="font-mono text-[11px] text-zinc-500 max-w-2xl">
          // powered by Stripe. Secure checkout. Instant activation. Cancel with one click from your account.
          {" "}
          <Link to="/app" className="text-signal-yellow hover:underline">← back to dashboard</Link>
        </p>
      )}
    </div>
  );
}
